import { useState } from "react";

import {
  Launch as LaunchIcon,
  MoreVert as MoreIcon,
  CheckCircle,
  Error as ErrorIcon,
  Apps as AppsIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import {
  Link,
  Button,
  Typography,
  Menu,
  MenuItem,
  Stack,
  Chip,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";

import { componentStyles } from "../styles/themeStyles";
import { ClusterStatus, ClusterApp } from "../types/cluster";

import AppDetails from "./appdetails";

export interface AppCardProps {
  app: ClusterApp;
  name?: string;
  c: ClusterStatus;
}

const AppCard = ({ app, name, c }: AppCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDetails = () => {
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Get namespace and app name
  const [namespace = "", appName = ""] = app.name.split("/");

  // Get app status
  const getStatusChip = () => {
    if (app.status.final && !app.status.error) {
      return (
        <Chip
          icon={<CheckCircle fontSize="small" />}
          label="Ready"
          color="success"
          size="small"
          sx={{
            ...componentStyles.statusChip,
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      );
    } else if (app.status.error) {
      return (
        <Chip
          icon={<ErrorIcon fontSize="small" />}
          label="Error"
          color="error"
          size="small"
          sx={{
            ...componentStyles.statusChip,
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      );
    } else {
      return (
        <Chip
          label="Deploying"
          color="warning"
          size="small"
          sx={{
            ...componentStyles.statusChip,
            fontSize: "0.75rem",
            height: 24,
          }}
        />
      );
    }
  };

  // Simple case: No links available
  if (!app.status.hrefs?.length) {
    return (
      <>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AppsIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="medium">
                {namespace}
              </Typography>
            </Stack>
            {getStatusChip()}
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No links available
            </Typography>

            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={handleOpenDetails}
                sx={{ color: "primary.main" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        <AppDetails
          app={app}
          cluster={c}
          open={detailsOpen}
          onClose={handleCloseDetails}
        />
      </>
    );
  }

  // Case: Single link
  if (app.status.hrefs?.length === 1) {
    const link = app.status.hrefs[0];
    return (
      <>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <AppsIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="medium">
                {namespace}
              </Typography>
            </Stack>
            {getStatusChip()}
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Link
              href={link}
              target="_blank"
              rel="noopener"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "primary.main",
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              <LaunchIcon fontSize="small" sx={{ mr: 0.5 }} />
              Open application
            </Link>

            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={handleOpenDetails}
                sx={{ color: "primary.main" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>

        <AppDetails
          app={app}
          cluster={c}
          open={detailsOpen}
          onClose={handleCloseDetails}
        />
      </>
    );
  }

  // Case: Multiple links
  return (
    <>
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <AppsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight="medium">
              {namespace}
            </Typography>
          </Stack>
          {getStatusChip()}
        </Stack>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {app.status.hrefs.length} endpoints available
          </Typography>

          <Stack direction="row" spacing={1}>
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={handleOpenDetails}
                sx={{ color: "primary.main" }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="View endpoints">
              <IconButton
                id="endpoints-button"
                aria-controls={open ? "endpoints-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                onClick={handleClick}
                size="small"
              >
                <MoreIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Menu
          id="endpoints-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "endpoints-button",
            dense: true,
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: {
              maxWidth: 300,
              maxHeight: 400,
              overflow: "auto",
            },
          }}
        >
          {app.status.hrefs.map((link, index) => (
            <MenuItem key={index} onClick={handleClose}>
              <Link
                href={link}
                target="_blank"
                rel="noopener"
                sx={{
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <LaunchIcon fontSize="small" sx={{ mr: 1, flexShrink: 0 }} />
                <Typography variant="body2" noWrap>
                  {new URL(link).hostname}
                </Typography>
              </Link>
            </MenuItem>
          ))}
        </Menu>
      </Stack>

      <AppDetails
        app={app}
        cluster={c}
        open={detailsOpen}
        onClose={handleCloseDetails}
      />
    </>
  );
};

export default AppCard;